import {
	IWorkOrderByCollectionId,
	IWorkOrderEvidenceByCollectionId,
} from '../common/data/dtos/work-order-by-collection-id';

/**
 * Aggregates all evidences from an array of work orders into a single
 * flat collection, preserving work-order input order.
 *
 * @param workOrders - Array of work orders with evidences
 * @returns Flat array of all evidences in work-order sequence order
 *
 * Examples:
 *   WO1 with 3 evidences, WO2 with 2 evidences → [e1, e2, e3, e4, e5]
 *   WO1 with 0 evidences, WO2 with 2 evidences → [e1, e2]
 */
export function aggregateEvidences(
	workOrders: IWorkOrderByCollectionId[],
): IWorkOrderEvidenceByCollectionId[] {
	const result: IWorkOrderEvidenceByCollectionId[] = [];

	for (const workOrder of workOrders) {
		if (workOrder.evidences.length === 0) {
			continue;
		}
		for (const evidence of workOrder.evidences) {
			result.push(evidence);
		}
	}

	return result;
}

/**
 * Calculates sequential photo ranges for each work order based on
 * cumulative evidence counts.
 *
 * @param workOrders - Array of work orders with evidences
 * @returns Array of range strings (same length/order as input)
 *
 * Examples:
 *   WO with 10 evidences, WO with 5 evidences → ["1 ~ 10", "11 ~ 15"]
 *   WO with 0 evidences, WO with 3 evidences → ["", "1 ~ 3"]
 */
export function calculatePhotoRanges(
	workOrders: Array<{ evidences: unknown[] }>,
): string[] {
	if (workOrders.length === 0) {
		return [];
	}

	const ranges: string[] = [];
	let cumulativeIndex = 0;

	for (const workOrder of workOrders) {
		const n = workOrder.evidences.length;

		if (n === 0) {
			ranges.push('');
		} else {
			const start = cumulativeIndex + 1;
			const end = start + n - 1;
			ranges.push(`${start} ~ ${end}`);
			cumulativeIndex = end;
		}
	}

	return ranges;
}
